import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Stepper, Step, StepLabel, Grid, TextField, Typography, Card, CardContent,
    CircularProgress, Divider, Chip
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import * as adminProductService from '../../services/admin-product.service';

class AddProductWizard extends Component {

    constructor(props) {
        super(props);
        this.state = {
            activeStep: 0,
            loading: false,
            confirming: false,

            // Step 0 — product selection
            products: [],
            selectedProduct: null,

            // Step 1 — plan + periodicity
            groupedPlans: [],
            selectedPlanId: 0,
            periodicities: [],
            selectedPeriodicity: null,

            // Step 2 — editable values
            planValuePerMonth: '',
            planValue: '',
            agentLimitQuantity: '',
            diskLimitUsage: '',
            attendanceLimitPerMonth: '',
            additionalValueAgent: '',
            attendanceValue: '',
            diskOverageValue: '',
            billingMode: 'unit',
            creditAmountMonthly: '',
            creditAlertThreshold: '',
            creditAutoRechargeEnabled: false,
            creditAutoRechargeAmount: '',
            creditAutoRechargeTrigger: '',
            quantity: 1,
        };
    }

    componentDidUpdate(prevProps) {
        if (this.props.open && !prevProps.open) {
            this.resetWizard();
            this.loadProducts();
        }
    }

    resetWizard = () => {
        this.setState({
            activeStep: 0, loading: false, confirming: false,
            products: [], selectedProduct: null,
            groupedPlans: [], selectedPlanId: 0, periodicities: [], selectedPeriodicity: null,
            planValuePerMonth: '', planValue: '', agentLimitQuantity: '', diskLimitUsage: '',
            attendanceLimitPerMonth: '', additionalValueAgent: '', attendanceValue: '',
            diskOverageValue: '', billingMode: 'unit', creditAmountMonthly: '', creditAlertThreshold: '',
            creditAutoRechargeEnabled: false, creditAutoRechargeAmount: '', creditAutoRechargeTrigger: '',
            quantity: 1,
        });
    };

    loadProducts = async () => {
        this.setState({ loading: true });
        try {
            const result = await adminProductService.getProducts(true);
            const raw = result && result.data && result.data.data ? result.data.data : [];
            const allProducts = Array.isArray(raw) ? raw : [];

            // Filter out already contracted products
            const contractedAppIds = (this.props.contractProducts || []).map(cp =>
                cp.applicationid ?? cp.applicationId ?? cp.ApplicationId ?? 0
            );
            const products = allProducts.filter(p => !contractedAppIds.includes(p.id));

            this.setState({ products, loading: false });
        } catch (err) {
            console.error('Error loading products', err);
            this.setState({ products: [], loading: false });
        }
    };

    handleSelectProduct = (product) => {
        this.setState({ selectedProduct: product, loading: true, activeStep: 1, groupedPlans: [], selectedPlanId: 0, periodicities: [], selectedPeriodicity: null });
        this.loadPlansForProduct(product.id);
    };

    loadPlansForProduct = async (applicationId) => {
        try {
            const result = await adminProductService.getPlanPeriodicities(null, applicationId, true);
            const raw = result && result.data && result.data.data ? result.data.data : [];
            const allPp = Array.isArray(raw) ? raw : [];

            const planMap = {};
            allPp.forEach(pp => {
                const pId = pp.planid ?? pp.planId ?? 0;
                const pName = pp.planname ?? pp.planName ?? '-';
                if (!planMap[pId]) planMap[pId] = { id: pId, name: pName, periodicities: [] };
                planMap[pId].periodicities.push(pp);
            });

            this.setState({ groupedPlans: Object.values(planMap), loading: false });
        } catch (err) {
            console.error('Error loading plans', err);
            this.setState({ groupedPlans: [], loading: false });
        }
    };

    handleSelectPlan = async (plan) => {
        const appId = this.state.selectedProduct ? this.state.selectedProduct.id : 0;
        this.setState({ selectedPlanId: plan.id, periodicities: [], selectedPeriodicity: null, loading: true });

        try {
            const result = await adminProductService.getPlanPeriodicities(plan.id, appId, true);
            const raw = result && result.data && result.data.data ? result.data.data : [];
            this.setState({ periodicities: Array.isArray(raw) ? raw : [], loading: false });
        } catch (err) {
            this.setState({ periodicities: plan.periodicities || [], loading: false });
        }
    };

    handleSelectPeriodicity = (pp) => {
        this.setState({
            selectedPeriodicity: pp,
            activeStep: 2,
            loading: true
        });
        this.loadPeriodicityDetail(pp.id ?? pp.Id);
    };

    loadPeriodicityDetail = async (planPeriodicityId) => {
        try {
            const result = await adminProductService.getPlanPeriodicityDetail(planPeriodicityId);
            const raw = result && result.data && result.data.data ? result.data.data : {};
            const d = raw.detail ?? raw.Detail ?? raw;
            this.setState({
                planValuePerMonth: d.planvaluepermonth ?? d.planValuePerMonth ?? '',
                planValue: d.planvalue ?? d.planValue ?? '',
                agentLimitQuantity: d.agentlimitquantity ?? d.agentLimitQuantity ?? '',
                diskLimitUsage: d.disklimitusage ?? d.diskLimitUsage ?? '',
                attendanceLimitPerMonth: d.attendancelimitpermonth ?? d.attendanceLimitPerMonth ?? '',
                additionalValueAgent: d.additionalvalueagent ?? d.additionalValueAgent ?? '',
                attendanceValue: d.attendancevalue ?? d.attendanceValue ?? '',
                diskOverageValue: d.diskoveragevalue ?? d.diskOverageValue ?? '',
                billingMode: d.billingmode ?? d.billingMode ?? 'unit',
                creditAmountMonthly: d.creditamountmonthly ?? d.creditAmountMonthly ?? '',
                creditAlertThreshold: d.creditalertthreshold ?? d.creditAlertThreshold ?? '',
                creditAutoRechargeEnabled: d.creditautorechargeenabled ?? d.creditAutoRechargeEnabled ?? false,
                creditAutoRechargeAmount: d.creditautorechargeamount ?? d.creditAutoRechargeAmount ?? '',
                creditAutoRechargeTrigger: d.creditautorechargetrigger ?? d.creditAutoRechargeTrigger ?? '',
                quantity: 1,
                loading: false
            });
        } catch (err) {
            this.setState({ loading: false });
        }
    };

    handleConfirm = () => {
        this.setState({ confirming: true });

        const { selectedProduct, selectedPeriodicity } = this.state;
        const selectedPlan = this.state.groupedPlans.find(p => p.id === this.state.selectedPlanId);

        this.props.onConfirm({
            applicationId: selectedProduct.id,
            planPeriodicityId: selectedPeriodicity.id ?? selectedPeriodicity.Id,
            planValuePerMonth: parseFloat(this.state.planValuePerMonth) || 0,
            planValue: parseFloat(this.state.planValue) || 0,
            agentLimitQuantity: parseInt(this.state.agentLimitQuantity, 10) || 0,
            diskLimitUsage: parseInt(this.state.diskLimitUsage, 10) || 0,
            attendanceLimitPerMonth: parseInt(this.state.attendanceLimitPerMonth, 10) || 0,
            additionalValueAgent: parseFloat(this.state.additionalValueAgent) || 0,
            attendanceValue: parseFloat(this.state.attendanceValue) || 0,
            diskOverageValue: parseFloat(this.state.diskOverageValue) || null,
            billingMode: this.state.billingMode || 'unit',
            creditAmountMonthly: parseFloat(this.state.creditAmountMonthly) || null,
            creditAlertThreshold: parseFloat(this.state.creditAlertThreshold) || null,
            creditAutoRechargeEnabled: this.state.creditAutoRechargeEnabled || false,
            creditAutoRechargeAmount: parseFloat(this.state.creditAutoRechargeAmount) || null,
            creditAutoRechargeTrigger: parseFloat(this.state.creditAutoRechargeTrigger) || null,
            quantity: parseInt(this.state.quantity, 10) || 1,
        });
    };

    handleFieldChange = (field) => (e) => {
        this.setState({ [field]: e.target.value });
    };

    fmt = (id) => {
        const { intl } = this.props;
        return intl && intl.formatMessage ? intl.formatMessage({ id }) : id;
    };

    formatCurrency = (value) => {
        if (value == null || value === '') return '-';
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    };

    // ==================== Render ====================

    renderStepContent = () => {
        switch (this.state.activeStep) {
            case 0: return this.renderProductSelection();
            case 1: return this.renderPlanSelection();
            case 2: return this.renderDetails();
            case 3: return this.renderConfirmation();
            default: return null;
        }
    };

    renderProductSelection = () => {
        const { products, selectedProduct, loading } = this.state;

        if (loading) {
            return <div style={{ textAlign: 'center', padding: 32 }}><CircularProgress /></div>;
        }

        if (products.length === 0) {
            return (
                <div style={{ textAlign: 'center', padding: 32 }}>
                    <ShoppingCartIcon style={{ fontSize: 48, color: '#bdbdbd', marginBottom: 8 }} />
                    <Typography variant="body1" color="textSecondary">{this.fmt('admin.subscription.no.available.products')}</Typography>
                </div>
            );
        }

        return (
            <div>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                    {this.fmt('admin.subscription.add.product.select')}
                </Typography>
                <Grid container spacing={2}>
                    {products.map((product) => {
                        const isSelected = selectedProduct && selectedProduct.id === product.id;
                        return (
                            <Grid item xs={12} sm={6} md={4} key={product.id}>
                                <Card
                                    elevation={isSelected ? 6 : 1}
                                    style={{
                                        cursor: 'pointer',
                                        border: isSelected ? '2px solid #8BC34A' : '1px solid #e0e0e0',
                                        backgroundColor: isSelected ? '#f1f8e9' : '#fff',
                                        transition: 'all 0.2s',
                                        height: '100%'
                                    }}
                                    onClick={() => this.handleSelectProduct(product)}
                                >
                                    <CardContent style={{ textAlign: 'center', padding: '24px 16px', position: 'relative' }}>
                                        {isSelected && <CheckCircleIcon style={{ position: 'absolute', top: 8, right: 8, fontSize: 20, color: '#8BC34A' }} />}
                                        <ShoppingCartIcon style={{ fontSize: 36, color: isSelected ? '#8BC34A' : '#9e9e9e', marginBottom: 8 }} />
                                        <Typography variant="subtitle1" style={{ fontWeight: 600, color: isSelected ? '#558B2F' : '#212121' }}>
                                            {product.name}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            </div>
        );
    };

    renderPlanSelection = () => {
        const { groupedPlans, selectedPlanId, periodicities, selectedPeriodicity, loading } = this.state;

        return (
            <div>
                {loading && <div style={{ textAlign: 'center', padding: 24 }}><CircularProgress size={28} /></div>}

                {!loading && (
                    <>
                        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                            {this.fmt('subscription.change.plan.select.new')} <strong>{this.state.selectedProduct?.name}</strong>
                        </Typography>

                        {/* Plan cards */}
                        <Typography variant="caption" style={{ fontWeight: 700, color: '#757575', textTransform: 'uppercase', letterSpacing: 1 }}>
                            {this.fmt('subscription.plan.name')}
                        </Typography>
                        <div style={{ display: 'flex', gap: 12, marginTop: 8, marginBottom: 20, flexWrap: 'wrap' }}>
                            {groupedPlans.map((plan, idx) => {
                                const isSelected = selectedPlanId === plan.id;
                                return (
                                    <Card key={idx} elevation={isSelected ? 6 : 1}
                                        style={{
                                            flex: '1 1 0', minWidth: 120, cursor: 'pointer',
                                            border: isSelected ? '2px solid #8BC34A' : '1px solid #e0e0e0',
                                            backgroundColor: isSelected ? '#f1f8e9' : '#fff',
                                            transition: 'all 0.2s'
                                        }}
                                        onClick={() => this.handleSelectPlan(plan)}
                                    >
                                        <CardContent style={{ textAlign: 'center', padding: '16px 12px', position: 'relative' }}>
                                            {isSelected && <CheckCircleIcon style={{ position: 'absolute', top: 8, right: 8, fontSize: 20, color: '#8BC34A' }} />}
                                            <Typography variant="subtitle1" style={{ fontWeight: 600, color: isSelected ? '#558B2F' : '#212121' }}>{plan.name}</Typography>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>

                        {/* Periodicity cards */}
                        {periodicities.length > 0 && (
                            <>
                                <Typography variant="caption" style={{ fontWeight: 700, color: '#757575', textTransform: 'uppercase', letterSpacing: 1 }}>
                                    {this.fmt('subscription.periodicity')}
                                </Typography>
                                <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
                                    {periodicities.map((pp, idx) => {
                                        const ppId = pp.id || pp.Id;
                                        const periodicityName = pp.periodicityname || pp.periodicityName || '-';
                                        const value = pp.planvaluepermonth || pp.planValuePerMonth || 0;
                                        const agents = pp.agentlimitquantity || pp.agentLimitQuantity;
                                        const attendances = pp.attendancelimitpermonth || pp.attendanceLimitPerMonth;
                                        const isSelected = selectedPeriodicity && (selectedPeriodicity.id || selectedPeriodicity.Id) === ppId;

                                        return (
                                            <Card key={idx} elevation={isSelected ? 6 : 1}
                                                style={{
                                                    flex: '1 1 0', minWidth: 160, cursor: 'pointer',
                                                    border: isSelected ? '2px solid #8BC34A' : '1px solid #e0e0e0',
                                                    backgroundColor: isSelected ? '#f1f8e9' : '#fff',
                                                    transition: 'all 0.2s'
                                                }}
                                                onClick={() => this.handleSelectPeriodicity(pp)}
                                            >
                                                <CardContent style={{ textAlign: 'center', padding: '16px 12px', position: 'relative' }}>
                                                    {isSelected && <CheckCircleIcon style={{ position: 'absolute', top: 8, right: 8, fontSize: 20, color: '#8BC34A' }} />}
                                                    <Typography variant="subtitle2" style={{ fontWeight: 600, color: isSelected ? '#558B2F' : '#212121' }}>{periodicityName}</Typography>
                                                    <Typography variant="h6" style={{ fontWeight: 700, color: isSelected ? '#558B2F' : '#212121', marginTop: 4 }}>
                                                        {this.formatCurrency(value)}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">/mês</Typography>
                                                    <Divider style={{ margin: '8px 0' }} />
                                                    {agents != null && <Typography variant="caption" style={{ display: 'block' }}>{agents} agentes</Typography>}
                                                    {attendances != null && <Typography variant="caption" style={{ display: 'block' }}>{attendances} atendimentos/mês</Typography>}
                                                </CardContent>
                                            </Card>
                                        );
                                    })}
                                </div>
                            </>
                        )}

                        {groupedPlans.length === 0 && !loading && (
                            <Typography variant="body2" color="textSecondary" style={{ textAlign: 'center', padding: 24 }}>
                                {this.fmt('add.product.no.plans')}
                            </Typography>
                        )}
                    </>
                )}
            </div>
        );
    };

    renderDetails = () => {
        const { loading } = this.state;

        if (loading) {
            return <div style={{ textAlign: 'center', padding: 32 }}><CircularProgress /></div>;
        }

        const qty = parseInt(this.state.quantity) || 1;
        const price = parseFloat(this.state.planValuePerMonth) || 0;

        return (
            <div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                    <Typography variant="subtitle2" style={{ fontWeight: 600, color: '#424242' }}>
                        {this.fmt('admin.subscription.edit.limits')}
                    </Typography>
                    <div style={{ marginLeft: 12, padding: '4px 12px', backgroundColor: '#f1f8e9', border: '1px solid #8BC34A', borderRadius: 16 }}>
                        <Typography variant="body2" style={{ fontWeight: 600, color: '#558B2F' }}>
                            {this.state.selectedProduct?.name} — {this.state.groupedPlans.find(p => p.id === this.state.selectedPlanId)?.name} / {this.state.selectedPeriodicity?.periodicityname || this.state.selectedPeriodicity?.periodicityName || ''}
                        </Typography>
                    </div>
                </div>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <TextField label={this.fmt('admin.subscription.price')} type="number" value={this.state.planValuePerMonth} onChange={this.handleFieldChange('planValuePerMonth')} fullWidth variant="outlined" size="small" inputProps={{ step: '0.01', min: '0' }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField label={this.fmt('admin.subscription.plan.value')} type="number" value={this.state.planValue} onChange={this.handleFieldChange('planValue')} fullWidth variant="outlined" size="small" inputProps={{ step: '0.01', min: '0' }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField label={this.fmt('admin.subscription.quantity')} type="number" value={this.state.quantity} onChange={this.handleFieldChange('quantity')} fullWidth variant="outlined" size="small" inputProps={{ min: '1' }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField label={this.fmt('admin.subscription.agents')} type="number" value={this.state.agentLimitQuantity} onChange={this.handleFieldChange('agentLimitQuantity')} fullWidth variant="outlined" size="small" inputProps={{ min: '0' }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField label={this.fmt('admin.subscription.disk')} type="number" value={this.state.diskLimitUsage} onChange={this.handleFieldChange('diskLimitUsage')} fullWidth variant="outlined" size="small" inputProps={{ min: '0' }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField label={this.fmt('admin.subscription.attendances')} type="number" value={this.state.attendanceLimitPerMonth} onChange={this.handleFieldChange('attendanceLimitPerMonth')} fullWidth variant="outlined" size="small" inputProps={{ min: '0' }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField label={this.fmt('admin.subscription.additional.agent')} type="number" value={this.state.additionalValueAgent} onChange={this.handleFieldChange('additionalValueAgent')} fullWidth variant="outlined" size="small" inputProps={{ step: '0.01', min: '0' }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField label={this.fmt('admin.subscription.attendance.value')} type="number" value={this.state.attendanceValue} onChange={this.handleFieldChange('attendanceValue')} fullWidth variant="outlined" size="small" inputProps={{ step: '0.00001', min: '0' }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField label={this.fmt('admin.pp.disk.overage.value')} type="number" value={this.state.diskOverageValue} onChange={this.handleFieldChange('diskOverageValue')} fullWidth variant="outlined" size="small" inputProps={{ step: '0.01', min: '0' }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField label={this.fmt('admin.pp.billing.mode')} value={this.state.billingMode === 'credit' ? 'Por Crédito' : 'Por Unidade'} fullWidth variant="outlined" size="small" disabled
                            InputProps={{ style: { backgroundColor: '#ebebeb', color: '#777' } }} />
                    </Grid>
                    {this.state.billingMode === 'credit' && (
                        <>
                            <Grid item xs={12}><Divider style={{ margin: '4px 0' }} /><Typography variant="caption" style={{ fontWeight: 700, color: '#757575', textTransform: 'uppercase' }}>{this.fmt('admin.pp.credit.section')}</Typography></Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField label={this.fmt('admin.pp.credit.monthly')} type="number" value={this.state.creditAmountMonthly} onChange={this.handleFieldChange('creditAmountMonthly')} fullWidth variant="outlined" size="small" inputProps={{ step: '0.01', min: '0' }} />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField label={this.fmt('admin.pp.credit.alert')} type="number" value={this.state.creditAlertThreshold} onChange={this.handleFieldChange('creditAlertThreshold')} fullWidth variant="outlined" size="small" inputProps={{ step: '0.01', min: '0' }} />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField select label={this.fmt('admin.pp.credit.auto.recharge')} value={this.state.creditAutoRechargeEnabled ? 'true' : 'false'} onChange={(e) => this.setState({ creditAutoRechargeEnabled: e.target.value === 'true' })} fullWidth variant="outlined" size="small" SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}>
                                    <option value="false">Não</option>
                                    <option value="true">Sim</option>
                                </TextField>
                            </Grid>
                            {this.state.creditAutoRechargeEnabled && (
                                <>
                                    <Grid item xs={12} sm={4}>
                                        <TextField label={this.fmt('admin.pp.credit.recharge.amount')} type="number" value={this.state.creditAutoRechargeAmount} onChange={this.handleFieldChange('creditAutoRechargeAmount')} fullWidth variant="outlined" size="small" inputProps={{ step: '0.01', min: '0' }} />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField label={this.fmt('admin.pp.credit.recharge.trigger')} type="number" value={this.state.creditAutoRechargeTrigger} onChange={this.handleFieldChange('creditAutoRechargeTrigger')} fullWidth variant="outlined" size="small" inputProps={{ step: '0.01', min: '0' }} />
                                    </Grid>
                                </>
                            )}
                        </>
                    )}
                </Grid>

                {/* Valor Total Calculado */}
                <div style={{
                    marginTop: 16, padding: '12px 20px', backgroundColor: '#f1f8e9',
                    border: '1px solid #8BC34A', borderRadius: 8,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                }}>
                    <Typography variant="subtitle2" style={{ color: '#558B2F', fontWeight: 600 }}>
                        {this.fmt('admin.subscription.total.value')}
                    </Typography>
                    <Typography variant="h5" style={{ fontWeight: 700, color: '#2e7d32' }}>
                        {this.formatCurrency(qty * price)}
                    </Typography>
                </div>
            </div>
        );
    };

    renderConfirmation = () => {
        const { selectedProduct, selectedPeriodicity, confirming } = this.state;
        const selectedPlan = this.state.groupedPlans.find(p => p.id === this.state.selectedPlanId);
        const qty = parseInt(this.state.quantity) || 1;
        const price = parseFloat(this.state.planValuePerMonth) || 0;

        if (confirming) {
            return (
                <div style={{ textAlign: 'center', padding: 32 }}>
                    <CircularProgress size={48} style={{ marginBottom: 16 }} />
                    <Typography variant="body1" color="textSecondary">Processando...</Typography>
                </div>
            );
        }

        const billingModeLabel = this.state.billingMode === 'credit' ? 'Por Crédito' : 'Por Unidade';

        const items = [
            { label: this.fmt('admin.contract.products.product'), value: selectedProduct?.name },
            { label: this.fmt('subscription.plan.name'), value: selectedPlan?.name },
            { label: this.fmt('subscription.periodicity'), value: selectedPeriodicity?.periodicityname || selectedPeriodicity?.periodicityName },
            { label: this.fmt('admin.pp.billing.mode'), value: billingModeLabel },
            { label: this.fmt('admin.subscription.price'), value: this.formatCurrency(this.state.planValuePerMonth) },
            { label: this.fmt('admin.subscription.plan.value'), value: this.formatCurrency(this.state.planValue) },
            { label: this.fmt('admin.subscription.quantity'), value: this.state.quantity },
            { label: this.fmt('admin.subscription.agents'), value: this.state.agentLimitQuantity || '-' },
            { label: this.fmt('admin.subscription.disk'), value: this.state.diskLimitUsage ? `${this.state.diskLimitUsage} MB` : '-' },
            { label: this.fmt('admin.pp.disk.overage.value'), value: this.formatCurrency(this.state.diskOverageValue) },
            { label: this.fmt('admin.subscription.attendances'), value: this.state.attendanceLimitPerMonth || '-' },
            { label: this.fmt('admin.subscription.additional.agent'), value: this.formatCurrency(this.state.additionalValueAgent) },
            { label: this.fmt('admin.subscription.attendance.value'), value: this.formatCurrency(this.state.attendanceValue) },
        ];

        if (this.state.billingMode === 'credit') {
            items.push(
                { label: this.fmt('admin.pp.credit.monthly'), value: this.formatCurrency(this.state.creditAmountMonthly) },
                { label: this.fmt('admin.pp.credit.alert'), value: this.formatCurrency(this.state.creditAlertThreshold) },
                { label: this.fmt('admin.pp.credit.auto.recharge'), value: this.state.creditAutoRechargeEnabled ? 'Sim' : 'Não' },
            );
            if (this.state.creditAutoRechargeEnabled) {
                items.push(
                    { label: this.fmt('admin.pp.credit.recharge.amount'), value: this.formatCurrency(this.state.creditAutoRechargeAmount) },
                    { label: this.fmt('admin.pp.credit.recharge.trigger'), value: this.formatCurrency(this.state.creditAutoRechargeTrigger) },
                );
            }
        }

        return (
            <div>
                <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 16, color: '#424242' }}>
                    {this.fmt('confirm')}
                </Typography>
                <Grid container spacing={1}>
                    {items.map((item, idx) => (
                        <Grid item xs={6} sm={4} key={idx}>
                            <Typography variant="caption" color="textSecondary">{item.label}</Typography>
                            <Typography variant="body2" style={{ fontWeight: 500 }}>{item.value || '-'}</Typography>
                        </Grid>
                    ))}
                </Grid>

                <div style={{
                    marginTop: 16, padding: '12px 20px', backgroundColor: '#f1f8e9',
                    border: '1px solid #8BC34A', borderRadius: 8,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                }}>
                    <Typography variant="subtitle2" style={{ color: '#558B2F', fontWeight: 600 }}>
                        {this.fmt('admin.subscription.total.value')}
                    </Typography>
                    <Typography variant="h5" style={{ fontWeight: 700, color: '#2e7d32' }}>
                        {this.formatCurrency(qty * price)}
                    </Typography>
                </div>
            </div>
        );
    };

    render() {
        const { open, onClose } = this.props;
        const { activeStep, confirming } = this.state;

        const stepLabels = [
            this.fmt('admin.subscription.add.product.select'),
            this.fmt('admin.products.pp.plan'),
            this.fmt('admin.subscription.edit.limits'),
            this.fmt('confirm')
        ];

        return (
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ style: { maxHeight: '90vh' } }}>
                <DialogTitle style={{ backgroundColor: '#8BC34A', color: '#fff' }}>
                    <ShoppingCartIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                    {this.fmt('admin.subscription.add.product')}
                </DialogTitle>
                <DialogContent dividers style={{ overflowY: 'auto', maxHeight: '70vh' }}>
                    <Stepper activeStep={activeStep} alternativeLabel style={{ paddingBottom: 24 }}>
                        {stepLabels.map((label, index) => (
                            <Step key={index}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                    {this.renderStepContent()}
                </DialogContent>
                <DialogActions style={{ padding: '12px 24px' }}>
                    {activeStep > 0 && !confirming && (
                        <Button onClick={() => this.setState(prev => ({ activeStep: prev.activeStep - 1 }))} style={{ textTransform: 'none' }}>
                            {this.fmt('back')}
                        </Button>
                    )}
                    <div style={{ flex: 1 }} />
                    <Button onClick={onClose} style={{ textTransform: 'none' }} disabled={confirming}>
                        {this.fmt('cancel')}
                    </Button>
                    {activeStep === 2 && (
                        <Button
                            variant="contained"
                            onClick={() => this.setState({ activeStep: 3 })}
                            style={{ backgroundColor: '#8BC34A', color: '#fff', textTransform: 'none', fontWeight: 600 }}
                        >
                            {this.fmt('subscription.continue')}
                        </Button>
                    )}
                    {activeStep === 3 && !confirming && (
                        <Button
                            variant="contained"
                            onClick={this.handleConfirm}
                            style={{ backgroundColor: '#8BC34A', color: '#fff', textTransform: 'none', fontWeight: 600 }}
                        >
                            {this.fmt('admin.subscription.add.product')}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        );
    }
}

AddProductWizard.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    hirerId: PropTypes.number,
    hirerName: PropTypes.string,
    contractProducts: PropTypes.array,
    intl: PropTypes.object.isRequired
};

AddProductWizard.defaultProps = {
    contractProducts: []
};

export default AddProductWizard;
